import React from 'react';
import { Button, ButtonGroup, Card, CardBody, CardImg, Nav, NavItem } from 'reactstrap';
import MaterialIcon from 'material-icons-react';
import FontAwesome from 'react-fontawesome';
import { Link } from 'react-router-dom';
import { compact, find, uniq } from 'underscore';
import numeral from 'numeral';
import cx from 'classnames';
import URI from 'urijs';
import moment from 'moment';
import { withState } from 'recompose';

// containers
import FavoriteSearchContactAgentModal from '../../containers/FavoriteSearch/FavoriteSearchContactAgentModal';

// components
import { ucFirst } from '../../utilities';

// modules
import BS from '../../theme/Bootstrap';
import containerStyles from '../../containers/Containers.module.scss';

// css
import styles from './property-card.module.scss';

// images
import notfound from './images/notfound.png';

const PHOTO_BASE_URL = process.env.REACT_APP_PHOTO_LARGE_URL;

const enhance = withState('showContactModal', 'setShowContactModal', false);
const PropertyCard = enhance(
  ({
    data,
    className,
    tag = Card,
    placeholderImg = notfound,
    searchURI,
    detailURI,
    availabilityType,
    isFavorite = false,
    onFavoriteClick,
    showContactModal,
    setShowContactModal
  }) => {
    const Tag = tag;

    const address = compact([
      (data.fullAddress || '').trim(),
      (data.city || '').trim(),
      compact([(data.stateOrProvince || '').trim(), (data.postalCode || '').trim()]).join(
        ' '
      )
    ]).join(', ');

    const location = uniq(compact([data.city, data.developmentName, data.condoName]));
    const { propertyClass } = data;
    const isRental = propertyClass === 'rental';
    const isCommercial = propertyClass === 'commercial';
    const isResidential = propertyClass === 'residential';
    const isResidentialIncome = propertyClass === 'residential_income';
    const isLand = propertyClass === 'land';

    return (
      <Tag
        className={`${className} ${styles.propertyCard} ${
          styles['propertyCard' + ucFirst(propertyClass)]
        }`}
      >
        <CardImg top tag="div" className={styles.imgWrap}>
          <Link
            to={URI(`/property-detail/${data.mlsNumber}-${propertyClass}`)
              .search({
                searchURI,
                detailURI
              })
              .toString()}
          >
            <img
              src={data.photoPath ? PHOTO_BASE_URL + data.photoPath : placeholderImg}
              alt="Property"
              className={cx(styles.img)}
              onError={e => {
                e.target.src = placeholderImg;
              }}
            />
          </Link>
        </CardImg>
        {isLand && (
          <ButtonGroup className={`${styles.nav}`}>
            <Button color="white" size="lg" className={BS.pointerEventsNone}>
              <FontAwesome fixedWidth name="building" /> {data.lotType}
            </Button>
            <Button color="white" size="lg" className={BS.pointerEventsNone}>
              <FontAwesome fixedWidth name="compass" />{' '}
              {numeral(data.acres).format('0,0')} Acres
            </Button>
          </ButtonGroup>
        )}
        {isCommercial && (
          <React.Fragment>
            <ButtonGroup className={`${styles.nav}`}>
              <Button color="white" size="lg" className={BS.pointerEventsNone}>
                <FontAwesome fixedWidth name="tag" />{' '}
                {data.propertyType.trim().split(',').join(', ')}
              </Button>
              <Button color="white" size="lg" className={BS.pointerEventsNone}>
                <FontAwesome fixedWidth name="building" />{' '}
                {numeral(data.subjectPropertySqFt).format('0,0')} sqft
              </Button>
            </ButtonGroup>
            <ButtonGroup className={`${styles.nav}`}>
              <Button color="white" size="lg" className={BS.pointerEventsNone}>
                <FontAwesome fixedWidth name="industry" />{' '}
                {data.commercialType.trim().split(',').join(', ')}
              </Button>
              <Button color="white" size="lg" className={BS.pointerEventsNone}>
                <FontAwesome fixedWidth name="exchange-alt" /> For {data.transactionType}
              </Button>
            </ButtonGroup>
          </React.Fragment>
        )}
        {(isResidential || isRental || isResidentialIncome) && (
          <ButtonGroup className={`${styles.nav}`}>
            <Button color="white" size="lg" className={BS.pointerEventsNone}>
              <FontAwesome fixedWidth name="bed" /> {data.bedrooms} Bed
            </Button>
            <Button color="white" size="lg" className={BS.pointerEventsNone}>
              <FontAwesome fixedWidth name="bath" /> {getBathString(data)}
            </Button>
            <Button color="white" size="lg" className={BS.pointerEventsNone}>
              <FontAwesome fixedWidth name="home" />{' '}
              {numeral(data.livingArea).format('0,0')} sqft
            </Button>
          </ButtonGroup>
        )}
        <CardBody className={`${styles.body}`}>
          <Button
            color="link"
            active={isFavorite}
            className={cx(BS.floatRight, BS.p0, containerStyles.favoritesLink, {
              [containerStyles.active]: isFavorite
            })}
            onClick={onFavoriteClick}
          >
            <MaterialIcon icon={'star_border'} />
            <MaterialIcon icon={'star'} />
          </Button>
          <p>
            {data.bldgDesign || <span>&nbsp;</span>}
            {isRental ? ' for Rent' : null}
            {isLand ? 'Land for Sale' : null}
          </p>
          <strong>{address || 'No Address'}</strong>
          <br />
          {location.join(' > ') || 'No Location'}
          <Nav
            horizontal="between"
            className={`${styles.detailNav} ${BS.p3} ${BS.textBlue} ${BS.fontWeightBold}`}
          >
            {data.isSold && (
              <NavItem className={BS.textOrange}>
                SOLD: {numeral(data.currentPrice).format('$0,0')}
              </NavItem>
            )}
            {data.status && !data.isSold && (
              <NavItem>
                {data.status.toLowerCase() === 'pending with contingencies'
                  ? 'Pending'
                  : data.status}
                : {getPrice(data, availabilityType)}
              </NavItem>
            )}
            {!data.isSold && !data.status && (
              <NavItem>{getPrice(data, availabilityType)}</NavItem>
            )}
            <NavItem>
              <Link
                to={URI(`/property-detail/${data.mlsNumber}-${propertyClass}`)
                  .search({
                    searchURI,
                    detailURI
                  })
                  .toString()}
              >
                View Details >
              </Link>
            </NavItem>
          </Nav>
          <div
            className={cx(
              BS.dFlex,
              BS.justifyContentSpaceBetween,
              BS.alignItemsCenter,
              BS.flexWrap
            )}
          >
            <div>
              Agent:{' '}
              <Link
                to={URI(`/agent/${data.displayAgent.id}`)
                  .search({
                    searchURI,
                    detailURI
                  })
                  .toString()}
              >
                {data.displayAgent.fullName}
              </Link>
              {data.displayAgent && data.displayAgent.directWorkPhone && (
                <React.Fragment>
                  <br />
                  Agent Phone:{' '}
                  <Button
                    color="link"
                    href={`tel://${data.displayAgent.directWorkPhone}`}
                    className={`${BS.p0} ${BS.textUnderline}`}
                  >
                    {data.displayAgent.directWorkPhone}
                  </Button>
                </React.Fragment>
              )}
              <br />
              Brokered by:{' '}
              <Link
                to={URI(`/firm/${data.listOfficeMLSID}`)
                  .search({
                    searchURI,
                    detailURI
                  })
                  .toString()}
              >
                {data.listOfficeName}
              </Link>
            </div>
            <div>{getNextOpenHouse(data.openhouses, setShowContactModal)}</div>
          </div>
        </CardBody>
        {showContactModal && (
          <FavoriteSearchContactAgentModal
            backdrop="static"
            isOpen={showContactModal}
            propertyClass={propertyClass}
            mlsIds={[data.mlsNumber]}
            onToggle={isOpen => setShowContactModal(false)}
          />
        )}
      </Tag>
    );
  }
);

function getNextOpenHouse(openhouses = [], setShowContactModal) {
  let openhouse = find(openhouses, function (openhouse) {
    return moment(openhouse.startDate).isSameOrAfter(moment().startOf('DAY'));
  });

  if (!openhouse) {
    return (
      <div>
        Next Open House
        <br />
        <Button color="link" size="sm" onClick={() => setShowContactModal(true)}>
          Contact Agent
        </Button>
      </div>
    );
  }

  const openhouseDate = compact([
    moment(openhouse.startDate).format('MM/DD/YY ha'),
    openhouse.endDate && moment(openhouse.endDate).format('ha')
  ]).join(' - ');

  return (
    <div>
      Next Open House:
      <br />
      {openhouseDate}
    </div>
  );
}

function getBathString(data) {
  var str = `${data.bathsFull || 0} (${data.bathsHalf || 0}) Bath`;
  return str;
}

function getPrice(data) {
  var price = data.currentPrice,
    label = '';

  if (data.propertyClass === 'rental') {
    if (!data.availabilityType || data.availabilityType.indexOf('Annual') !== -1) {
      price = data.monthlyRate || price;
      label = ' / Month';
    } else if (data.availabilityType.indexOf('Seasonal') !== -1) {
      price = data.seasonRate;
      label = ' / Month';
    } else if (data.availabilityType.indexOf('Off-Season') !== -1) {
      price = data.offSeasonRate;
      label = ' / Month';
    }
  }

  return `${numeral(price).format('$0,0')}${label}`;
}

export default PropertyCard;



// WEBPACK FOOTER //
// ./src/partials/property-card/property-card.js